import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { apiFetch } from "../utils/api";
import { useTheme } from "../context/ThemeContext";

const PAGE_SIZE = 50;

const READINESS_STYLES = {
  READY:                { bg: "#d1fae5", color: "#065f46", label: "Ready" },
  TRIED_BUT_DIDNT_PASS: { bg: "#ffedd5", color: "#9a3412", label: "Tried" },
  ABSENT:               { bg: "#fee2e2", color: "#991b1b", label: "Absent" },
};

const prefersReducedMotion =
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const spring = prefersReducedMotion
  ? { duration: 0 }
  : { type: "spring", stiffness: 300, damping: 28 };

const fadeUp = prefersReducedMotion
  ? {}
  : { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

/* ── Skeleton ─────────────────────────────────────────────────────── */
function Skeleton({ className = "" }) {
  return (
    <div className={`animate-pulse rounded-lg bg-gray-200 ${className}`} />
  );
}

/* ── Mini stat strip card ─────────────────────────────────────────── */
function MiniStat({ label, value, color, bg, loading }) {
  return (
    <div
      className="flex-1 min-w-[100px] rounded-2xl border border-gray-100 bg-white shadow-sm px-4 py-3"
    >
      <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">
        {label}
      </p>
      {loading ? (
        <Skeleton className="h-6 w-12" />
      ) : (
        <p className="text-2xl font-black" style={{ color }}>
          {value}
        </p>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════════ */
function StudentsPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const [readinessMap, setReadinessMap] = useState({});

  const debounceRef = useRef(null);

  useEffect(() => {
    if (!localStorage.getItem("user")) navigate("/login", { replace: true });
  }, []);

  useEffect(() => {
    fetchStudents(searchTerm, page);
  }, [page]);

  const handleSearch = useCallback((value) => {
    setSearchTerm(value);
    setPage(1);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchStudents(value, 1), 350);
  }, []);

  const fetchStudents = async (search = "", pg = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: PAGE_SIZE });
      if (search.trim()) params.set("search", search.trim());
      const res = await apiFetch(`/students?${params}`);
      const data = await res.json();
      if (data.success) {
        const list = data.data || [];
        setStudents(list);
        setMeta(data.meta || { total: 0, pages: 1 });
        if (list.length > 0) {
          const ids = list.map((s) => s.id).join(",");
          apiFetch(`/readiness-checks/student-statuses?studentIds=${ids}`)
            .then((r) => r.json())
            .then((r) => { if (r.success) setReadinessMap(r.data || {}); })
            .catch(() => {});
        }
      }
    } catch {
      setMessage("Failed to load students");
    } finally {
      setLoading(false);
    }
  };

  const deleteStudent = async (studentId) => {
    if (!window.confirm("Are you sure you want to delete this student?")) return;
    try {
      const res = await apiFetch(`/students/${studentId}`, { method: "DELETE" });
      if (res.ok) {
        setMessage("success:Student deleted successfully");
        fetchStudents(searchTerm, page);
      } else {
        setMessage("error:Failed to delete student");
      }
    } catch {
      setMessage("error:Error deleting student");
    }
  };

  const downloadRegistrationForm = () => {
    const today = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
    const field = (label, height = "16pt") =>
      `<tr>
        <td style="width:140pt;padding:2pt 5pt;font-size:9pt;font-weight:bold;color:#1e293b;border:0.75pt solid #cbd5e1;background:#f1f5f9;">${label}</td>
        <td style="padding:2pt 5pt;border:0.75pt solid #cbd5e1;height:${height};"></td>
      </tr>`;
    const sectionHeader = (title) =>
      `<tr><td colspan="2" style="background:#1e3a5f;color:white;font-size:9pt;font-weight:bold;padding:3pt 6pt;border:0.75pt solid #1e3a5f;">${title}</td></tr>`;

    const html = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">
<head>
  <meta charset="UTF-8">
  <!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:DoNotOptimizeForBrowser/></w:WordDocument></xml><![endif]-->
  <style>
    @page { size: A4 portrait; margin: 1.2cm 1.5cm; }
    body { font-family: Calibri, Arial, sans-serif; color: #1e293b; font-size: 9pt; }
    table { border-collapse: collapse; width: 100%; }
    p { margin: 0; }
  </style>
</head>
<body>

  <!-- Header -->
  <div style="text-align:center;margin-bottom:6pt;padding-bottom:5pt;border-bottom:2pt solid #1e3a5f;">
    <p style="font-size:15pt;font-weight:bold;color:#1e3a5f;">Student Registration Form</p>
    <p style="font-size:8pt;color:#64748b;margin-top:2pt;">Please fill in all fields clearly using block letters &nbsp;·&nbsp; ${today}</p>
  </div>

  <!-- Student ID -->
  <table style="margin-bottom:5pt;">
    <tr>
      <td style="width:50%;padding:2pt 5pt;font-size:9pt;font-weight:bold;color:#1e293b;border:0.75pt solid #cbd5e1;background:#f1f5f9;">Student ID (office use)</td>
      <td style="width:50%;padding:2pt 5pt;border:0.75pt solid #cbd5e1;height:16pt;"></td>
    </tr>
  </table>

  <!-- PERSONAL INFORMATION -->
  <table style="margin-bottom:5pt;">
    ${sectionHeader("1. Personal Information")}
    ${field("First Name")}
    ${field("Last Name")}
    ${field("Date of Birth (DD/MM/YYYY)")}
    ${field("Phone Number")}
  </table>

  <!-- PARENT / GUARDIAN -->
  <table style="margin-bottom:5pt;">
    ${sectionHeader("2. Parent / Guardian Information")}
    ${field("Parent Full Name")}
    ${field("Parent Phone (primary)")}
    ${field("Parent Phone (secondary)")}
  </table>

  <!-- ACADEMIC INFORMATION -->
  <table style="margin-bottom:5pt;">
    ${sectionHeader("3. Academic Information")}
    ${field("Preferred Group / Schedule")}
    <tr>
      <td style="width:140pt;padding:2pt 5pt;font-size:9pt;font-weight:bold;color:#1e293b;border:0.75pt solid #cbd5e1;background:#f1f5f9;">How did you hear about us?</td>
      <td style="padding:2pt 5pt;border:0.75pt solid #cbd5e1;font-size:9pt;height:16pt;">
        ☐ Friend &nbsp; ☐ Instagram &nbsp; ☐ Telegram &nbsp; ☐ Banner &nbsp; ☐ Other: ___________
      </td>
    </tr>
    ${field("Referred by (name)")}
  </table>

  <!-- PAYMENT -->
  <table style="margin-bottom:5pt;">
    ${sectionHeader("4. Payment Information")}
    ${field("Enrollment Date (DD/MM/YYYY)")}
  </table>

  <!-- NOTES -->
  <table style="margin-bottom:5pt;">
    ${sectionHeader("5. Additional Notes")}
    ${field("Internal Notes (office use)", "28pt")}
  </table>

  <!-- Signatures -->
  <table style="margin-top:8pt;">
    <tr>
      <td style="width:100%;padding:4pt 3pt;font-size:8.5pt;color:#475569;border-top:0.75pt solid #94a3b8;">
        Registered by (Staff): _________________________
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        Date: ___________________
      </td>
    </tr>
  </table>

</body>
</html>`;

    const blob = new Blob(["\ufeff", html], { type: "application/msword" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "student-registration-form.doc";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadExport = async () => {
    try {
      const res = await apiFetch("/students/export");
      if (!res.ok) { setMessage("error:Export failed"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `students-export-${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setMessage("error:Error exporting students");
    }
  };

  /* derived */
  const activeCount  = students.filter((s) => s.status === "ACTIVE").length;
  const isSuccess    = message.startsWith("success:");
  const messageText  = message.replace(/^(success|error):/, "");

  /* page number array for pagination (max 7 buttons) */
  const pageNumbers = (() => {
    if (meta.pages <= 7) return Array.from({ length: meta.pages }, (_, i) => i + 1);
    const nums = new Set([1, 2, page - 1, page, page + 1, meta.pages - 1, meta.pages]);
    return [...nums].filter((n) => n >= 1 && n <= meta.pages).sort((a, b) => a - b);
  })();

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.bgPage }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">

        {/* ── Page header ──────────────────────────────────────────── */}
        <motion.div
          {...fadeUp}
          transition={{ ...spring, delay: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">
              Admin
            </p>
            <h1
              className="text-3xl sm:text-4xl font-black tracking-tight"
              style={{ color: theme.primary }}
            >
              Students
            </h1>
            {!loading && (
              <p className="text-sm text-gray-400 mt-1 font-medium">
                {meta.total.toLocaleString()} students registered
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate("/students/add")}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm text-white shadow-sm hover:shadow-md transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2"
              style={{ backgroundColor: theme.primary, "--tw-ring-color": theme.primary }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              Add Student
            </button>
            <button
              onClick={downloadExport}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm text-white shadow-sm hover:shadow-md transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-400"
              style={{ backgroundColor: "#6366f1" }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Export CSV
            </button>
            <button
              onClick={() => navigate("/import")}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm text-white shadow-sm hover:shadow-md transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-400"
              style={{ backgroundColor: "#10b981" }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
              Import
            </button>
            <button
              onClick={downloadRegistrationForm}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm text-white shadow-sm hover:shadow-md transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-400"
              style={{ backgroundColor: "#d97706" }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
              Reg Form
            </button>
          </div>
        </motion.div>

        {/* ── Stat strip ───────────────────────────────────────────── */}
        <motion.div
          {...fadeUp}
          transition={{ ...spring, delay: 0.05 }}
          className="flex gap-3 flex-wrap"
        >
          <MiniStat label="Total" value={meta.total.toLocaleString()} color={theme.primary} loading={loading} />
          <MiniStat label="Active (this page)" value={activeCount} color="#10b981" loading={loading} />
          <MiniStat label="Page" value={`${page} / ${meta.pages}`} color="#6366f1" loading={loading} />
        </motion.div>

        {/* ── Search bar ───────────────────────────────────────────── */}
        <motion.div
          {...fadeUp}
          transition={{ ...spring, delay: 0.08 }}
          className="relative"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            type="text"
            placeholder="Search by name, ID, phone or parent phone…"
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white rounded-2xl border border-gray-100 shadow-sm text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow"
            style={{ "--tw-ring-color": theme.primary }}
          />
          {searchTerm && (
            <button
              onClick={() => handleSearch("")}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Clear search"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </motion.div>

        {/* ── Toast message ─────────────────────────────────────────── */}
        <AnimatePresence>
          {message && (
            <motion.div
              key="msg"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="rounded-xl px-4 py-3 text-sm font-semibold border"
              style={{
                backgroundColor: isSuccess ? "#f0fdf4" : "#fef2f2",
                borderColor:     isSuccess ? "#bbf7d0" : "#fecaca",
                color:           isSuccess ? "#166534" : "#991b1b",
              }}
            >
              {messageText}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Student table ─────────────────────────────────────────── */}
        <motion.div
          {...fadeUp}
          transition={{ ...spring, delay: 0.12 }}
        >
          {loading ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : students.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-16 flex flex-col items-center gap-3 text-gray-300">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-14 h-14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
              </svg>
              <p className="text-base font-semibold text-gray-400">
                {searchTerm ? "No students match your search" : "No students yet"}
              </p>
              {!searchTerm && (
                <p className="text-sm text-gray-400">Import students to get started</p>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2 border-gray-100">
                      <th className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-widest text-gray-400">
                        ID
                      </th>
                      <th className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-widest text-gray-400">
                        Name
                      </th>
                      <th className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-widest text-gray-400 hidden sm:table-cell">
                        Phone
                      </th>
                      <th className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-widest text-gray-400">
                        Status
                      </th>
                      <th className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-widest text-gray-400 hidden md:table-cell">
                        Groups
                      </th>
                      <th className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-widest text-gray-400 hidden lg:table-cell">
                        Readiness
                      </th>
                      <th className="px-4 py-3.5 text-right text-[11px] font-bold uppercase tracking-widest text-gray-400">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {students.map((student, idx) => {
                      const rdns = readinessMap[student.id];
                      const rdStyle = rdns ? READINESS_STYLES[rdns.status] : null;
                      return (
                        <motion.tr
                          key={student.id}
                          initial={prefersReducedMotion ? {} : { opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: idx * 0.015 }}
                          onClick={() => navigate(`/students/${student.id}`)}
                          className="cursor-pointer hover:bg-gray-50/80 transition-colors duration-100 group"
                        >
                          {/* ID */}
                          <td className="px-4 py-3.5">
                            <span className="font-mono text-xs text-gray-400 group-hover:text-gray-600 transition-colors">
                              {student.studentId}
                            </span>
                          </td>

                          {/* Name + email */}
                          <td className="px-4 py-3.5">
                            <p className="font-semibold text-sm text-gray-800 leading-tight">
                              {student.user?.firstName} {student.user?.lastName}
                            </p>
                            {student.user?.email && (
                              <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[180px]">
                                {student.user.email}
                              </p>
                            )}
                          </td>

                          {/* Phone */}
                          <td className="px-4 py-3.5 hidden sm:table-cell">
                            <span className="text-sm text-gray-600">
                              {student.user?.phoneNumber || (
                                <span className="text-gray-300">—</span>
                              )}
                            </span>
                          </td>

                          {/* Status */}
                          <td className="px-4 py-3.5">
                            <span
                              className="inline-block text-[11px] font-bold px-2.5 py-1 rounded-full"
                              style={{
                                backgroundColor: student.status === "ACTIVE" ? "#d1fae5" : "#fee2e2",
                                color:           student.status === "ACTIVE" ? "#059669" : "#dc2626",
                              }}
                            >
                              {student.status}
                            </span>
                          </td>

                          {/* Groups */}
                          <td className="px-4 py-3.5 hidden md:table-cell">
                            {student.enrollments?.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {student.enrollments.map((e) => (
                                  <span
                                    key={e.id}
                                    className="text-[11px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap"
                                    style={{ backgroundColor: "#eff6ff", color: "#2563eb", borderColor: "#bfdbfe" }}
                                  >
                                    {e.group.name}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-300">No group</span>
                            )}
                          </td>

                          {/* Readiness */}
                          <td className="px-4 py-3.5 hidden lg:table-cell">
                            {rdStyle ? (
                              <span
                                title={`${rdns.groupName} · ${rdns.checkTitle}`}
                                className="text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap"
                                style={{ backgroundColor: rdStyle.bg, color: rdStyle.color }}
                              >
                                {rdStyle.label}
                              </span>
                            ) : (
                              <span className="text-gray-300 text-xs">—</span>
                            )}
                          </td>

                          {/* Delete */}
                          <td
                            className="px-4 py-3.5 text-right"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => deleteStudent(student.id)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-red-600 bg-red-50 border border-red-100 hover:bg-red-100 transition-colors duration-100 focus:outline-none focus:ring-2 focus:ring-red-300"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                              </svg>
                              Delete
                            </button>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* ── Pagination ───────────────────────────────────── */}
              {meta.pages > 1 && (
                <div className="flex items-center justify-between px-4 py-3.5 border-t border-gray-100">
                  <p className="text-xs text-gray-400 font-medium hidden sm:block">
                    {((page - 1) * PAGE_SIZE + 1).toLocaleString()}–
                    {Math.min(page * PAGE_SIZE, meta.total).toLocaleString()} of{" "}
                    {meta.total.toLocaleString()} students
                  </p>

                  <div className="flex items-center gap-1 mx-auto sm:mx-0">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="px-3 py-1.5 rounded-lg text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      ←
                    </button>

                    {pageNumbers.map((n, i) => {
                      const prev = pageNumbers[i - 1];
                      const showEllipsis = prev && n - prev > 1;
                      return (
                        <React.Fragment key={n}>
                          {showEllipsis && (
                            <span className="px-1 text-gray-300 text-sm select-none">…</span>
                          )}
                          <button
                            onClick={() => setPage(n)}
                            className="w-8 h-8 rounded-lg text-sm font-bold transition-colors focus:outline-none"
                            style={
                              n === page
                                ? { backgroundColor: theme.primary, color: "white" }
                                : { color: "#6b7280" }
                            }
                          >
                            {n}
                          </button>
                        </React.Fragment>
                      );
                    })}

                    <button
                      onClick={() => setPage((p) => Math.min(meta.pages, p + 1))}
                      disabled={page >= meta.pages}
                      className="px-3 py-1.5 rounded-lg text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      →
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.div>

      </div>
    </div>
  );
}

export default StudentsPage;
